/**
 * Phase 3.28 — TariffSection (Free/Basic/Pro Auswahl + Stripe-Checkout).
 */
import { useEffect, useState } from "react";
import { Sparkles, CheckCircle2, BadgeEuro } from "lucide-react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";

function formatPrice(cents, currency = "EUR") {
    if (cents == null) return "—";
    try {
        return new Intl.NumberFormat("de-DE", { style: "currency", currency, minimumFractionDigits: cents % 100 === 0 ? 0 : 2 }).format(cents / 100);
    } catch {
        return `${(cents / 100).toFixed(2)} €`;
    }
}

export function TariffSection({ currentUser, embedded = false }) {
    const [tariff, setTariff] = useState(null);
    const [loading, setLoading] = useState(true);
    const [allTariffs, setAllTariffs] = useState([]);
    const [upgrading, setUpgrading] = useState(null);
    const [portalOpening, setPortalOpening] = useState(false);

    useEffect(() => {
        let alive = true;
        Promise.all([
            api.get("/users/me/tariff"),
            api.get("/tariffs"),
        ])
            .then(([t, all]) => {
                if (!alive) return;
                setTariff(t.data?.tariff || null);
                setAllTariffs(all.data?.items || []);
            })
            .catch(() => { if (alive) setTariff(null); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser?.tariff_id]);

    const subStatus = currentUser?.subscription_status;
    const hasActiveSubscription = subStatus === "active" || subStatus === "trialing";

    const otherTariffs = (allTariffs || []).filter((t) => {
        if (!t.is_active) return false;
        if (t.monthly_price_cents <= 0) return false;
        if (tariff && t.id === tariff.id) return false;
        return true;
    });

    const startCheckout = async (targetTariff) => {
        setUpgrading(targetTariff.id);
        try {
            const r = await api.post("/users/me/stripe/checkout", {
                tariff_id: targetTariff.id,
                origin_url: window.location.origin,
            });
            if (r.data?.url) {
                window.location.href = r.data.url;
            } else {
                toast.error("Keine Checkout-URL erhalten");
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Checkout konnte nicht gestartet werden");
        } finally {
            setUpgrading(null);
        }
    };

    const openPortal = async () => {
        setPortalOpening(true);
        try {
            const r = await api.post("/users/me/stripe/portal", { origin_url: window.location.origin });
            if (r.data?.url) {
                window.location.href = r.data.url;
            } else {
                toast.error("Keine Portal-URL erhalten");
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Stripe-Portal nicht verfügbar");
        } finally {
            setPortalOpening(false);
        }
    };

    const wrapperClass = embedded ? "" : "bg-white border border-gray-200 rounded-sm p-6";

    return (
        <div className={wrapperClass} data-testid="tariff-section">
            {!embedded && (
                <div className="flex items-center gap-2 mb-3">
                    <BadgeEuro className="w-5 h-5 text-[#FFCC00]" />
                    <h3 className="font-display font-bold text-lg">Tarif</h3>
                </div>
            )}
            {loading ? (
                <div className="text-xs text-gray-500" data-testid="tariff-loading">Lade Tarif…</div>
            ) : tariff ? (
                <div data-testid="tariff-card">
                    <div className="flex items-baseline justify-between flex-wrap gap-2">
                        <div>
                            <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Aktueller Tarif</div>
                            <div className="font-display text-2xl font-black" data-testid="tariff-name">{tariff.name}</div>
                            {subStatus && (
                                <div className="mt-1 text-[11px] uppercase tracking-wider font-bold" data-testid="subscription-status">
                                    {subStatus === "active" && <span className="text-green-700">● Aktiv</span>}
                                    {subStatus === "trialing" && <span className="text-blue-700">● Testphase</span>}
                                    {subStatus === "past_due" && <span className="text-amber-700">● Zahlung überfällig</span>}
                                    {subStatus === "canceled" && <span className="text-gray-500">● Gekündigt</span>}
                                    {!["active","trialing","past_due","canceled"].includes(subStatus) && (
                                        <span className="text-gray-500">● {subStatus}</span>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="text-right">
                            <div className="text-2xl font-black font-mono-num text-[#FFCC00]" data-testid="tariff-price">
                                {formatPrice(tariff.monthly_price_cents, tariff.currency)}
                            </div>
                            <div className="text-[10px] uppercase tracking-wider text-gray-500">pro Monat</div>
                        </div>
                    </div>
                    {tariff.description && (
                        <p className="text-xs text-gray-600 mt-3" data-testid="tariff-description">{tariff.description}</p>
                    )}
                    {tariff.features?.length > 0 && (
                        <ul className="mt-4 space-y-1.5" data-testid="tariff-features">
                            {tariff.features.map((f, i) => (
                                <li key={i} className="flex items-start gap-2 text-xs text-[#111827]">
                                    <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 text-green-600 shrink-0" />
                                    <span>{f}</span>
                                </li>
                            ))}
                        </ul>
                    )}

                    {/* Upgrade buttons */}
                    {otherTariffs.length > 0 && (
                        <div className="mt-5 pt-4 border-t border-gray-100" data-testid="tariff-upgrade-section">
                            <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-2">
                                Wechsel auf einen anderen Tarif
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {otherTariffs.map((t) => (
                                    <button
                                        key={t.id}
                                        onClick={() => startCheckout(t)}
                                        disabled={upgrading === t.id}
                                        className="border border-gray-300 hover:border-[#FFCC00] hover:bg-[#FFFBEB] rounded-sm p-3 text-left disabled:opacity-50 transition-colors"
                                        data-testid={`tariff-upgrade-${t.slug}`}
                                    >
                                        <div className="flex items-baseline justify-between gap-2">
                                            <span className="font-bold text-sm text-[#111827]">{t.name}</span>
                                            <span className="font-mono-num text-sm font-bold text-[#FFCC00]">
                                                {formatPrice(t.monthly_price_cents, t.currency)}
                                            </span>
                                        </div>
                                        {t.description && <div className="text-[11px] text-gray-600 mt-0.5 line-clamp-2">{t.description}</div>}
                                        <div className="text-[10px] uppercase tracking-wider font-bold text-[#111827] mt-1">
                                            {upgrading === t.id ? "Stripe öffnet…" : "→ Wechseln & zahlen"}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Customer portal */}
                    {hasActiveSubscription && (
                        <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between flex-wrap gap-2">
                            <div className="text-[11px] text-gray-600">
                                Karte ändern, Rechnungen ansehen oder kündigen — sicher über Stripe.
                            </div>
                            <Button
                                onClick={openPortal}
                                disabled={portalOpening}
                                variant="outline"
                                size="sm"
                                className="rounded-sm font-bold uppercase tracking-wider text-xs"
                                data-testid="tariff-stripe-portal"
                            >
                                {portalOpening ? "Lädt…" : "Abo verwalten"}
                            </Button>
                        </div>
                    )}
                </div>
            ) : (
                <div data-testid="tariff-empty">
                    <div className="text-sm text-gray-600 border border-dashed border-gray-300 rounded-sm p-4">
                        <div className="font-bold text-[#111827] mb-1">Noch kein Tarif zugewiesen</div>
                        <div className="text-xs">
                            Wählen Sie unten einen Tarif, um sofort durchzustarten. Die Zahlung läuft sicher über Stripe.
                        </div>
                    </div>
                    {allTariffs.filter((t) => t.is_active && t.monthly_price_cents > 0).length > 0 && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3" data-testid="tariff-empty-options">
                            {allTariffs.filter((t) => t.is_active && t.monthly_price_cents > 0).map((t) => (
                                <button
                                    key={t.id}
                                    onClick={() => startCheckout(t)}
                                    disabled={upgrading === t.id}
                                    className="border border-gray-300 hover:border-[#FFCC00] hover:bg-[#FFFBEB] rounded-sm p-3 text-left disabled:opacity-50 transition-colors"
                                    data-testid={`tariff-upgrade-${t.slug}`}
                                >
                                    <div className="flex items-baseline justify-between gap-2">
                                        <span className="font-bold text-sm">{t.name}</span>
                                        <span className="font-mono-num text-sm font-bold text-[#FFCC00]">
                                            {formatPrice(t.monthly_price_cents, t.currency)}
                                        </span>
                                    </div>
                                    {t.description && <div className="text-[11px] text-gray-600 mt-0.5 line-clamp-2">{t.description}</div>}
                                    <div className="text-[10px] uppercase tracking-wider font-bold mt-1">
                                        {upgrading === t.id ? "Stripe öffnet…" : "→ Jetzt buchen"}
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}


// ============================================================================
// SOZIALMEDIA-Veröffentlichungen (Phase 3.16) — 4 Kanäle (WhatsApp, Instagram,
// TikTok, Facebook). Pro Kanal: Toggle (enabled) → bei aktiv erscheint ein
// Sub-Block mit zwei Modi (shared = TwoWheels-Zugang nutzen / own = eigener
// Händler-Zugang). Bei "own" werden Username / Token / Notiz erfasst.
// ============================================================================
const SOCIAL_CHANNELS = [
    { key: "whatsapp", label: "WhatsApp", hint: "Inserate als Status posten / Broadcast-Liste." },
    { key: "instagram", label: "Instagram", hint: "Reels & Posts auf Ihrem Business-Account." },
    { key: "tiktok", label: "TikTok", hint: "Kurzvideos & Slideshows für Ihr Sortiment." },
    { key: "facebook", label: "Facebook", hint: "Posts auf Ihrer Händler-Page + Marketplace." },
];

// Phase 3.28 — SocialMediaExportSection ausgelagert.
export { SocialMediaExportSection } from "./SocialMediaExportSection";
